import { Button, ButtonGroup, notify, SidebarPanel, SidebarPart, Text } from '@eleveo/components-react';
import { BinIcon } from '@eleveo/icons-react';
import React, { useCallback, useState } from 'react';
import { flushSync } from 'react-dom';
import { useFormState } from 'react-final-form';
import { Trans, useTranslation } from 'react-i18next';

import { useDeleteConfigurationById } from '../../../api/integrationsQuery';
import { useIntegrations } from '../../../app/IntegrationsProvider';
import { TEST_IDS } from '../../../constants/dataTestIds';
import { useSearchNavigate } from '../../../hooks/useSearchNavigate';
import { ConfigurationType } from '../../../scene/integrations/types';
import { INTEGRATIONS_WITH_ROLE_MAPPING } from '../../configuration/types';
import { useIntegrationRoles } from '../../forms/roles/IntegrationRolesProvider';
import { BlockingWarningModal } from '../../modals/BlockingWarningModal';
import { WarningModal } from '../../modals/WarningModal';

interface IntegrationActionsProps {
  beforeSubmit?: () => void;
}

export const IntegrationsHeader: React.FC<IntegrationActionsProps> = ({ beforeSubmit }) => {
  const { t } = useTranslation();
  const { isInReadOnlyMode, isInEditMode, canDeleteConfiguration, integrationType } = useIntegrations();
  const { integrationRoles, isIntegrationRolesLoading, isIntegrationRolesError } = useIntegrationRoles();

  const { navigateToMyConfigurations } = useSearchNavigate();

  const [isDeleteModalActive, setIsDeleteModalActive] = useState(false);
  const { mutate: deleteIntegration, isLoading: isDeleteInProgress, isSuccess } = useDeleteConfigurationById();

  const {
    values: formState,
    dirty: isFormDirty,
    hasValidationErrors,
    submitting: isFormSubmitting,
    initialValues: formInitialValues,
  } = useFormState<ConfigurationType>({
    subscription: {
      values: true,
      dirty: true,
      hasValidationErrors: true,
      submitting: true,
      initialValues: true,
    },
  });

  const handleDeleteClick = useCallback(() => {
    if (!canDeleteConfiguration) {
      notify.warning(t('missingPermissions'));
      return;
    }

    setIsDeleteModalActive(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canDeleteConfiguration]);

  const handleSideBarClose = () => {
    navigateToMyConfigurations();
  };

  const handleDelete = useCallback(() => {
    if (!isInEditMode || isDeleteInProgress || !canDeleteConfiguration) {
      return;
    }

    deleteIntegration(formState, {
      onSuccess: () => {
        notify.success(t('myConfigurations.response.successDelete'));
        flushSync(() => {
          setIsDeleteModalActive(false);
        });
        handleSideBarClose();
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeleteInProgress, isInEditMode, isInReadOnlyMode, formState]);

  const closeSideBarWithAnimation = () =>
    new Promise<void>((resolve) => {
      resolve();
    });

  // TODO: 3. 5. 2023 (david.salamon) ->  remove conflicted roles temporary field
  const isRoleMappingSupported = (INTEGRATIONS_WITH_ROLE_MAPPING as readonly string[]).includes(
    integrationType.toLowerCase()
  );
  const isIntegrationRolesFailed =
    isRoleMappingSupported && (!integrationRoles?.length || isIntegrationRolesLoading || isIntegrationRolesError);
  const isSubmitDisabled = isInReadOnlyMode || hasValidationErrors || isIntegrationRolesFailed || !isFormDirty;

  return (
    <>
      {/* prevents leaving without saving changes */}
      <BlockingWarningModal
        when={!isFormSubmitting && isFormDirty && !(isSuccess || isDeleteInProgress)}
        title={t('myConfigurations.unsavedChanges.title')}
        acceptCaption={t('myConfigurations.unsavedChanges.yes')}
        cancelCaption={t('myConfigurations.unsavedChanges.cancel')}
        onAccept={closeSideBarWithAnimation}
      >
        <Text>{t('myConfigurations.unsavedChanges.description')}</Text>
      </BlockingWarningModal>

      {/* shows dialog before delete */}
      <WarningModal
        isOpen={isDeleteModalActive}
        title={t('myConfigurations.deleteIntegration.title')}
        cancelCaption={t('myConfigurations.deleteIntegration.cancel')}
        acceptCaption={t('myConfigurations.deleteIntegration.yes')}
        onCancel={() => setIsDeleteModalActive(false)}
        onAccept={handleDelete}
      >
        <Text className="whitespace-nowrap">
          <Trans i18nKey="myConfigurations.deleteIntegration.description">
            Are you sure, you want to
            <strong>
              {/* // TODO: 5. 5. 2023 (david.salamon) ->  check source of this type mismatch */}
              delete &ldquo;<>{{ configurationName: formInitialValues.configurationName }}</>&rdquo;
            </strong>
          </Trans>
        </Text>
      </WarningModal>

      <SidebarPanel>
        <SidebarPart>
          <ButtonGroup>
            <Button
              color="success"
              type="submit"
              onClick={beforeSubmit}
              isDisabled={isSubmitDisabled}
              testId={TEST_IDS.buttonSaveIntegration}
            >
              {t('myConfigurations.save')}
            </Button>
            <Button
              color="warning"
              variant="outline"
              onClick={handleSideBarClose}
              testId={TEST_IDS.buttonCancelIntegration}
            >
              {t('myConfigurations.cancel')}
            </Button>
            {isInEditMode && (
              <Button
                className="mis-auto"
                color="error"
                variant="outline"
                onClick={handleDeleteClick}
                testId={TEST_IDS.buttonDeleteIntegration}
                aria-label={t('myConfigurations.delete')}
              >
                <BinIcon />
              </Button>
            )}
          </ButtonGroup>
        </SidebarPart>
      </SidebarPanel>
    </>
  );
};
